import { Controller } from "@hotwired/stimulus";

interface ModelEntry {
  model: string;
  modality: string;
}

/**
 * Hero Code Controller
 *
 * Orchestrates the homepage code example animation:
 * - Cycles through models with a typewriter effect on the model name
 * - Line-by-line typewriter rewrite when the modality changes (image → video → chat)
 * - Crossfade for SDK tab switching (JavaScript / Python / cURL)
 * - Pre-rendered <template> elements provide syntax-highlighted content for each modality
 */
export default class extends Controller<HTMLElement> {
  static targets = ["tab", "codeBlock", "container", "model", "modalitySource"];
  static values = {
    entries: { type: Array, default: [] },
    interval: { type: Number, default: 4000 },
    typeSpeed: { type: Number, default: 50 },
    deleteSpeed: { type: Number, default: 30 },
    lineTypeSpeed: { type: Number, default: 8 },
    lineDeleteSpeed: { type: Number, default: 5 },
    pauseDelay: { type: Number, default: 1500 },
  };

  declare readonly tabTargets: HTMLElement[];
  declare readonly codeBlockTargets: HTMLElement[];
  declare readonly modelTargets: HTMLElement[];
  declare readonly hasContainerTarget: boolean;
  declare readonly containerTarget: HTMLElement;
  declare readonly modalitySourceTargets: HTMLElement[];
  declare entriesValue: ModelEntry[];
  declare intervalValue: number;
  declare typeSpeedValue: number;
  declare deleteSpeedValue: number;
  declare lineTypeSpeedValue: number;
  declare lineDeleteSpeedValue: number;
  declare pauseDelayValue: number;

  private currentSDK: string = "javascript";
  private currentModality: string = "image";
  private currentIndex: number = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private isAnimating: boolean = false;
  private isSwitchingSDK: boolean = false;

  connect(): void {
    if (this.entriesValue.length === 0) return;

    this.currentModality = this.entriesValue[0]?.modality || "image";
    this.updateTabStyles("javascript");
    this.showInitialBlock();

    this.intervalId = setInterval(() => {
      this.cycleModel();
    }, this.intervalValue);
  }

  disconnect(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // --- SDK Tab Switching ---

  switchSDK(event: Event): void {
    const target = event.currentTarget as HTMLElement;
    const sdk = target.dataset.sdk;
    if (!sdk || sdk === this.currentSDK || this.isSwitchingSDK) return;
    this.animateSDKSwitch(sdk);
  }

  private animateSDKSwitch(newSDK: string): void {
    this.isSwitchingSDK = true;
    this.updateTabStyles(newSDK);

    const outgoing = this.findCodeBlock(this.currentSDK);
    const incoming = this.findCodeBlock(newSDK);
    if (!incoming) {
      this.isSwitchingSDK = false;
      return;
    }

    // Ensure the incoming block has the current modality content
    this.applyModalityContent(incoming, newSDK, this.currentModality, this.getCurrentModel());

    if (!outgoing) {
      incoming.classList.remove("hidden");
      incoming.style.opacity = "1";
      this.currentSDK = newSDK;
      this.isSwitchingSDK = false;
      return;
    }

    this.crossfadeBlocks(outgoing, incoming, () => {
      this.currentSDK = newSDK;
      this.isSwitchingSDK = false;
    });
  }

  private updateTabStyles(activeSDK: string): void {
    this.tabTargets.forEach((tab) => {
      const isActive = tab.dataset.sdk === activeSDK;
      if (isActive) {
        tab.classList.add(
          "bg-base-content/10",
          "text-base-content",
          "shadow-sm"
        );
        tab.classList.remove(
          "text-base-content/50",
          "hover:text-base-content/80",
          "hover:bg-base-content/5"
        );
      } else {
        tab.classList.remove(
          "bg-base-content/10",
          "text-base-content",
          "shadow-sm"
        );
        tab.classList.add(
          "text-base-content/50",
          "hover:text-base-content/80",
          "hover:bg-base-content/5"
        );
      }
    });
  }

  // --- Initial Display ---

  private showInitialBlock(): void {
    this.codeBlockTargets.forEach((block) => {
      if (block.dataset.sdk === this.currentSDK) {
        block.classList.remove("hidden");
        block.style.opacity = "1";
      } else {
        block.classList.add("hidden");
        block.style.opacity = "0";
      }
    });
  }

  // --- Model Cycling ---

  private async cycleModel(): Promise<void> {
    if (this.isAnimating || this.isSwitchingSDK) return;
    this.isAnimating = true;

    this.currentIndex = (this.currentIndex + 1) % this.entriesValue.length;
    const entry = this.entriesValue[this.currentIndex];

    if (entry.modality === this.currentModality) {
      // Same modality — typewriter the model name
      const currentText = this.getVisibleModelTargets()[0]?.textContent || "";
      await this.deleteModelText(currentText);
      await this.pause(200);
      await this.typeModelText(entry.model);
      await this.pause(this.pauseDelayValue);
    } else {
      // Different modality — line-by-line rewrite
      await this.modalityTransition(entry.model, entry.modality);
    }

    this.isAnimating = false;
  }

  // --- Modality Transition (line-by-line typewriter) ---

  private async modalityTransition(
    newModel: string,
    newModality: string
  ): Promise<void> {
    const visibleBlock = this.findCodeBlock(this.currentSDK);
    if (!visibleBlock) return;

    // Get new content from template
    const newPres = this.getModalityPres(
      this.currentSDK,
      newModality,
      newModel
    );
    if (!newPres.length) return;

    const currentPres = Array.from(visibleBlock.querySelectorAll(":scope > pre"));

    // Find first line that differs
    let firstDiff = 0;
    for (let i = 0; i < Math.min(currentPres.length, newPres.length); i++) {
      const currentText = currentPres[i].querySelector("code")?.textContent || "";
      const newText = newPres[i].querySelector("code")?.textContent || "";
      if (currentText !== newText) {
        firstDiff = i;
        break;
      }
    }

    // Animate height transition if line count changes
    const container = this.hasContainerTarget
      ? this.containerTarget
      : this.element;
    const startHeight = visibleBlock.offsetHeight;

    // Phase 1: Rewrite each changing line top-to-bottom
    const maxLen = Math.max(currentPres.length, newPres.length);

    for (let i = firstDiff; i < maxLen; i++) {
      const currentPre = currentPres[i] as HTMLElement | undefined;
      const newPre = newPres[i] as HTMLElement | undefined;

      if (currentPre && newPre) {
        // Line exists in both — erase old, type new
        await this.eraseLineContent(currentPre);
        const targetCode = newPre.querySelector("code")!;
        await this.typeLineContent(
          currentPre,
          targetCode.innerHTML,
          targetCode.textContent || ""
        );
        currentPre.setAttribute(
          "data-prefix",
          newPre.getAttribute("data-prefix") || ""
        );
      } else if (!currentPre && newPre) {
        // Line added — create empty pre, then type content
        const pre = document.createElement("pre");
        pre.setAttribute(
          "data-prefix",
          newPre.getAttribute("data-prefix") || ""
        );
        pre.innerHTML = "<code></code>";
        visibleBlock.appendChild(pre);
        const targetCode = newPre.querySelector("code")!;
        await this.typeLineContent(
          pre,
          targetCode.innerHTML,
          targetCode.textContent || ""
        );
      } else if (currentPre && !newPre) {
        // Line removed — erase then remove
        await this.eraseLineContent(currentPre);
        currentPre.remove();
      }
    }

    // Smooth height adjustment after line count change
    const endHeight = visibleBlock.offsetHeight;
    if (startHeight !== endHeight) {
      container.style.height = `${startHeight}px`;
      container.style.overflow = "hidden";
      container.style.transition =
        "height 200ms cubic-bezier(0.33, 1, 0.68, 1)";
      // Force reflow
      container.offsetHeight;
      container.style.height = `${endHeight}px`;
      await this.pause(200);
      container.style.height = "";
      container.style.overflow = "";
      container.style.transition = "";
    }

    // Update hidden SDK blocks instantly so SDK switching shows correct content
    this.codeBlockTargets.forEach((block) => {
      if (block.dataset.sdk !== this.currentSDK) {
        this.applyModalityContent(
          block,
          block.dataset.sdk!,
          newModality,
          newModel
        );
      }
    });

    this.currentModality = newModality;
    await this.pause(this.pauseDelayValue);
  }

  // --- Line-level Typewriter ---

  private eraseLineContent(pre: HTMLElement): Promise<void> {
    return new Promise((resolve) => {
      const code = pre.querySelector("code")!;
      const text = code.textContent || "";
      let index = text.length;

      // Switch to plain text for the erase animation
      code.textContent = text;

      const erase = () => {
        if (index > 0) {
          index--;
          code.textContent = text.substring(0, index);
          setTimeout(erase, this.lineDeleteSpeedValue);
        } else {
          resolve();
        }
      };

      erase();
    });
  }

  private typeLineContent(
    pre: HTMLElement,
    targetHtml: string,
    targetText: string
  ): Promise<void> {
    return new Promise((resolve) => {
      const code = pre.querySelector("code")!;
      let index = 0;

      const type = () => {
        if (index < targetText.length) {
          index++;
          code.textContent = targetText.substring(0, index);
          setTimeout(type, this.lineTypeSpeedValue);
        } else {
          // Snap to syntax-highlighted HTML
          code.innerHTML = targetHtml;
          resolve();
        }
      };

      type();
    });
  }

  // --- Model Name Typewriter ---

  private getVisibleModelTargets(): HTMLElement[] {
    const visibleBlock = this.findCodeBlock(this.currentSDK);
    if (!visibleBlock) return [];
    return Array.from(
      visibleBlock.querySelectorAll<HTMLElement>(
        '[data-hero-code-target="model"]'
      )
    );
  }

  private deleteModelText(text: string): Promise<void> {
    return new Promise((resolve) => {
      let index = text.length;
      const targets = this.getVisibleModelTargets();

      const deleteChar = () => {
        if (index > 0) {
          index--;
          const newText = text.substring(0, index);
          targets.forEach((t) => (t.textContent = newText));
          setTimeout(deleteChar, this.deleteSpeedValue);
        } else {
          resolve();
        }
      };

      deleteChar();
    });
  }

  private typeModelText(text: string): Promise<void> {
    return new Promise((resolve) => {
      let index = 0;
      const targets = this.getVisibleModelTargets();

      const typeChar = () => {
        if (index < text.length) {
          const newText = text.substring(0, index + 1);
          targets.forEach((t) => (t.textContent = newText));
          index++;
          setTimeout(typeChar, this.typeSpeedValue);
        } else {
          resolve();
        }
      };

      typeChar();
    });
  }

  // --- Crossfade (for SDK switching) ---

  private crossfadeBlocks(
    outgoing: HTMLElement,
    incoming: HTMLElement,
    onComplete: () => void
  ): void {
    const startHeight = outgoing.offsetHeight;

    incoming.classList.remove("hidden");
    incoming.style.opacity = "0";
    incoming.style.position = "absolute";
    incoming.style.visibility = "hidden";
    const endHeight = incoming.offsetHeight;
    incoming.style.position = "";
    incoming.style.visibility = "";

    const container = this.hasContainerTarget
      ? this.containerTarget
      : this.element;
    container.style.height = `${startHeight}px`;
    container.style.overflow = "hidden";
    container.style.transition =
      "height 200ms cubic-bezier(0.33, 1, 0.68, 1)";

    outgoing.style.transition = "opacity 150ms ease-out";
    outgoing.style.opacity = "0";

    setTimeout(() => {
      outgoing.classList.add("hidden");
      outgoing.style.transition = "";

      container.style.height = `${endHeight}px`;

      incoming.style.transition = "opacity 150ms ease-in";
      incoming.style.opacity = "1";

      setTimeout(() => {
        container.style.height = "";
        container.style.overflow = "";
        container.style.transition = "";
        incoming.style.transition = "";
        onComplete();
      }, 200);
    }, 150);
  }

  // --- Template & Content Helpers ---

  private getModalityPres(
    sdk: string,
    modality: string,
    model: string
  ): HTMLElement[] {
    const template = this.modalitySourceTargets.find(
      (t) => t.dataset.sdk === sdk && t.dataset.modality === modality
    ) as HTMLTemplateElement | undefined;
    if (!template) return [];

    const fragment = template.content.cloneNode(true) as DocumentFragment;

    // Set the correct model name
    const modelSpan = fragment.querySelector(
      '[data-hero-code-target="model"]'
    );
    if (modelSpan) modelSpan.textContent = model;

    return Array.from(fragment.querySelectorAll("pre")) as HTMLElement[];
  }

  private applyModalityContent(
    block: HTMLElement,
    sdk: string,
    modality: string,
    model: string
  ): void {
    const template = this.modalitySourceTargets.find(
      (t) => t.dataset.sdk === sdk && t.dataset.modality === modality
    ) as HTMLTemplateElement | undefined;
    if (!template) return;

    const fragment = template.content.cloneNode(true) as DocumentFragment;
    const modelSpan = fragment.querySelector(
      '[data-hero-code-target="model"]'
    );
    if (modelSpan) modelSpan.textContent = model;

    // Replace all <pre> lines
    block.querySelectorAll(":scope > pre").forEach((pre) => pre.remove());
    block.appendChild(fragment);
  }

  private getCurrentModel(): string {
    const entry = this.entriesValue[this.currentIndex];
    return entry?.model || "";
  }

  private findCodeBlock(sdk: string): HTMLElement | undefined {
    return this.codeBlockTargets.find((b) => b.dataset.sdk === sdk);
  }

  private pause(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
